// src/features/secureDocuments/validation/sepaSofortAusfuehrung.ts
//
// Stellt den Ausführungstermin einer SEPA-Überweisungsdatei (pain.001) um.
// Anlass: DATEV setzt den Ausführungstermin des Zahlungslaufs, und dieser
// liegt beim Upload in den geschützten Downloadbereich oft in der Zukunft.
// Soll die Datei sofort ausgeführt werden, setzt das Upload-Modal den Termin
// auf den Versandtag (Sofortversand: heute, geplanter Versand: Tag des
// gewählten Zeitpunkts, jeweils Berliner Datum).
//
// Geändert wird ausschließlich der Datumswert in ReqdExctnDt, und zwar in
// jedem Zahlungsblock (PmtInf) der Datei. Die Datei wird nicht neu aufgebaut,
// sondern Byte für Byte übernommen; überschrieben werden nur die zehn Zeichen
// des Datums. Kodierung, Formatierung, Beträge, Konten und Verwendungszwecke
// bleiben dadurch unverändert. Unterstützt sind die beiden im Bestand
// vorkommenden Formen: pain.001.001.03 (Datum direkt im Element) und
// pain.001.001.09 (Datum im Unterelement Dt). Ein Termin mit Uhrzeit (DtTm)
// oder jede andere unerwartete Form wird nicht angefasst, sondern als Fehler
// gemeldet. Der Aufrufer lädt in diesem Fall nichts hoch.
//
// Nach der Umstellung wird das Ergebnis gegengeprüft: gleiche Länge,
// Abweichungen nur innerhalb der Datumsfelder, lesbares XML und in jedem
// Zahlungsblock der neue Termin.
//
// Die Funktion ist idempotent: Trägt die Datei bereits den gewünschten
// Termin, kommt sie inhaltlich unverändert zurück.
//
// Das Modul ist bewusst frei von Oberflächenbezug, damit es später auch in
// der DATEV-Automatisierung verwendet werden kann.

export type AusfuehrungsterminErgebnis =
  | { ok: true; datei: File; anzahlZahlungsbloecke: number }
  | { ok: false; fehler: string };

const DATUM = /^\d{4}-\d{2}-\d{2}$/;

// Präfix eines Namensraums, etwa "ns2:". DATEV schreibt ohne Präfix, andere
// Programme gelegentlich mit.
const PRAEFIX = '(?:[A-Za-z_][\\w.-]*:)?';

const UEBERWEISUNG = new RegExp(`<${PRAEFIX}CstmrCdtTrfInitn(?:\\s[^>]*)?>`);

// Öffnendes Tag eines Zahlungsblocks. Das Tag PmtInfId wird nicht erfasst,
// weil nach "PmtInf" nur ein Leerraum oder das Tag-Ende folgen darf.
const ZAHLUNGSBLOCK = new RegExp(`<${PRAEFIX}PmtInf(?:\\s[^>]*)?>`, 'g');

const TERMIN_ELEMENT = new RegExp(
  `<(${PRAEFIX})ReqdExctnDt(?:\\s[^>]*)?>([\\s\\S]*?)</\\1ReqdExctnDt\\s*>`,
  'g'
);

// Inhalt in pain.001.001.03: nur das Datum, gegebenenfalls mit Leerraum.
const INHALT_DIREKT = /^(\s*)(\d{4}-\d{2}-\d{2})\s*$/;

// Inhalt in pain.001.001.09: das Datum im Unterelement Dt. DtTm wird nicht
// erfasst, weil nach "Dt" nur ein Leerraum oder das Tag-Ende folgen darf.
const INHALT_DT = new RegExp(
  `^(\\s*<(${PRAEFIX})Dt(?:\\s[^>]*)?>\\s*)(\\d{4}-\\d{2}-\\d{2})\\s*</\\2Dt\\s*>\\s*$`
);

/**
 * Bildet jedes Byte auf genau ein Zeichen ab. Damit entsprechen Positionen im
 * Text exakt Positionen in der Datei, unabhängig von ihrer Kodierung.
 */
function bytesZuZeichen(bytes: Uint8Array): string {
  let text = '';
  const block = 0x8000;
  for (let i = 0; i < bytes.length; i += block) {
    text += String.fromCharCode(...Array.from(bytes.subarray(i, i + block)));
  }
  return text;
}

/** Kodierung laut XML-Deklaration, ohne Angabe UTF-8. */
function kodierung(text: string): string {
  const treffer = text.slice(0, 200).match(/^\s*<\?xml[^>]*encoding\s*=\s*["']([^"']+)["']/i);
  return treffer ? treffer[1] : 'utf-8';
}

/** Liest die umgestellte Datei als XML und prüft jeden Zahlungsblock. */
function gegenpruefung(bytes: Uint8Array, rohtext: string, neuerTermin: string, anzahl: number): string | null {
  let text: string;
  try {
    text = new TextDecoder(kodierung(rohtext)).decode(bytes);
  } catch {
    text = new TextDecoder('utf-8').decode(bytes);
  }

  const dom = new DOMParser().parseFromString(text, 'application/xml');
  if (dom.getElementsByTagName('parsererror').length > 0) {
    return 'Die umgestellte Datei ist kein lesbares XML mehr.';
  }

  const bloecke = dom.getElementsByTagNameNS('*', 'PmtInf');
  if (bloecke.length !== anzahl) {
    return `Nach der Umstellung ${bloecke.length} statt ${anzahl} Zahlungsblöcke gelesen.`;
  }

  const termine = dom.getElementsByTagNameNS('*', 'ReqdExctnDt');
  if (termine.length !== anzahl) {
    return `Nach der Umstellung ${termine.length} statt ${anzahl} Ausführungstermine gelesen.`;
  }
  for (let i = 0; i < termine.length; i++) {
    const wert = (termine[i].textContent || '').trim();
    if (wert !== neuerTermin) {
      return `Nach der Umstellung steht im Zahlungsblock ${i + 1} der Termin "${wert}" statt ${neuerTermin}.`;
    }
  }

  return null;
}

/**
 * Setzt den Ausführungstermin aller Zahlungsblöcke einer SEPA-Überweisungs-
 * datei auf neuerTermin (YYYY-MM-DD). Liefert die umgestellte Datei unter
 * demselben Namen oder eine Fehlerbeschreibung.
 */
export async function setzeAusfuehrungstermin(
  datei: File,
  neuerTermin: string
): Promise<AusfuehrungsterminErgebnis> {
  if (!DATUM.test(neuerTermin)) {
    return { ok: false, fehler: `Der Zieltermin "${neuerTermin}" ist kein gültiges Datum.` };
  }

  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await datei.arrayBuffer());
  } catch (fehler) {
    return {
      ok: false,
      fehler: `Die Datei konnte nicht gelesen werden: ${fehler instanceof Error ? fehler.message : 'unbekannter Fehler'}.`
    };
  }

  const text = bytesZuZeichen(bytes);

  if (!UEBERWEISUNG.test(text)) {
    return { ok: false, fehler: 'Die Datei ist keine SEPA-Überweisungsdatei.' };
  }

  const anzahlBloecke = (text.match(ZAHLUNGSBLOCK) || []).length;
  if (anzahlBloecke === 0) {
    return { ok: false, fehler: 'Die Datei enthält keinen Zahlungsblock.' };
  }

  // Positionen der zehn Datumszeichen je Ausführungstermin bestimmen.
  const positionen: number[] = [];
  TERMIN_ELEMENT.lastIndex = 0;
  let treffer: RegExpExecArray | null;
  while ((treffer = TERMIN_ELEMENT.exec(text)) !== null) {
    const inhalt = treffer[2];
    const inhaltBeginn = treffer.index + treffer[0].indexOf('>') + 1;

    const direkt = inhalt.match(INHALT_DIREKT);
    if (direkt) {
      positionen.push(inhaltBeginn + direkt[1].length);
      continue;
    }
    const mitDt = inhalt.match(INHALT_DT);
    if (mitDt) {
      positionen.push(inhaltBeginn + mitDt[1].length);
      continue;
    }
    return {
      ok: false,
      fehler: 'Der Ausführungstermin liegt in einer unerwarteten Form vor (zum Beispiel mit Uhrzeit) und wird deshalb nicht verändert.'
    };
  }

  if (positionen.length !== anzahlBloecke) {
    return {
      ok: false,
      fehler: `Die Datei enthält ${anzahlBloecke} Zahlungsblöcke, aber ${positionen.length} lesbare Ausführungstermine.`
    };
  }

  // Nur die Datumszeichen überschreiben, alle übrigen Bytes bleiben erhalten.
  const neu = bytes.slice();
  for (const position of positionen) {
    for (let i = 0; i < 10; i++) {
      neu[position + i] = neuerTermin.charCodeAt(i);
    }
  }

  // Gegenprüfung 1: gleiche Länge, Abweichungen nur in den Datumsfeldern.
  if (neu.length !== bytes.length) {
    return { ok: false, fehler: 'Die Dateigröße hat sich bei der Umstellung verändert.' };
  }
  for (let i = 0; i < bytes.length; i++) {
    if (neu[i] === bytes[i]) continue;
    const imDatum = positionen.some(position => i >= position && i < position + 10);
    if (!imDatum) {
      return { ok: false, fehler: `Bei der Umstellung hat sich Byte ${i} außerhalb eines Datumsfelds verändert.` };
    }
  }

  // Gegenprüfung 2: lesbares XML mit dem neuen Termin in jedem Zahlungsblock.
  const problem = gegenpruefung(neu, text, neuerTermin, anzahlBloecke);
  if (problem) {
    return { ok: false, fehler: problem };
  }

  return {
    ok: true,
    datei: new globalThis.File([neu], datei.name, { type: datei.type, lastModified: datei.lastModified }),
    anzahlZahlungsbloecke: anzahlBloecke
  };
}
